import React, { useState } from 'react';
import { CourseApi, SpeedReadingCourse, CreateCoursePayload } from './trainingApi';

interface CourseAdminDialogProps {
  initialCourse?: SpeedReadingCourse | null;
  onClose: () => void;
  onSaved: (course: SpeedReadingCourse) => void;
}

const CourseAdminDialog: React.FC<CourseAdminDialogProps> = ({ initialCourse, onClose, onSaved }) => {
  const isEdit = Boolean(initialCourse);
  const [title, setTitle] = useState(initialCourse?.title || '');
  const [description, setDescription] = useState(initialCourse?.description || '');
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(initialCourse?.coverMedia?.url || null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCoverChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setCoverFile(file);
    if (file) {
      const url = URL.createObjectURL(file);
      setCoverPreview(url);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) { setError('Başlık zorunludur'); return; }

    setSaving(true);
    setError(null);
    try {
      const payload: Partial<CreateCoursePayload> = {
        title: title.trim(),
        description: description.trim() || undefined,
        cover: coverFile || undefined,
      };

      let saved: SpeedReadingCourse;
      if (isEdit && initialCourse) {
        saved = await CourseApi.update(initialCourse.id, payload);
      } else {
        saved = await CourseApi.create(payload as CreateCoursePayload);
      }
      onSaved(saved);
    } catch (err: any) {
      setError(err?.message || 'Kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0b1f29]/50 p-4 backdrop-blur-sm">
      <div className="w-full max-w-lg overflow-hidden rounded-[2rem] bg-white shadow-[0_30px_80px_rgba(11,31,41,0.25)]">
        <div className="border-b border-[#ecf2f5] bg-gradient-to-r from-[#f0f8fa] to-[#f7fbfc] px-6 py-5">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#0ea5b7]">Yönetim</p>
              <h2 className="mt-0.5 text-xl font-black text-[#174e67]">
                {isEdit ? 'Ana Dersi Düzenle' : 'Yeni Ana Ders'}
              </h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="flex h-9 w-9 items-center justify-center rounded-full bg-[#eef6f8] text-[#174e67] transition hover:bg-[#dff0f3]"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m18 6-12 12M6 6l12 12"/></svg>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="flex flex-col gap-5">
            <label className="block">
              <span className="text-[11px] font-black uppercase tracking-[0.15em] text-[#67808c]">Ana Ders Adı *</span>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="örn. Hızlı Okuma Adım Adım"
                className="mt-2 w-full rounded-2xl border border-[#dbe7ec] bg-[#f7fbfc] px-4 py-3 text-sm font-semibold text-[#1d4758] outline-none placeholder:text-[#9fb3bc] focus:border-[#0ea5b7] focus:bg-white"
              />
            </label>

            <label className="block">
              <span className="text-[11px] font-black uppercase tracking-[0.15em] text-[#67808c]">Açıklama</span>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Bu ana dersin konusu ve amacı…"
                rows={3}
                className="mt-2 w-full resize-y rounded-2xl border border-[#dbe7ec] bg-[#f7fbfc] px-4 py-3 text-sm font-semibold leading-6 text-[#1d4758] outline-none placeholder:text-[#9fb3bc] focus:border-[#0ea5b7] focus:bg-white"
              />
            </label>

            <div>
              <span className="text-[11px] font-black uppercase tracking-[0.15em] text-[#67808c]">Kapak Görseli</span>
              <label className="mt-2 flex cursor-pointer items-center gap-4 rounded-2xl border border-dashed border-[#cfe2e8] bg-[#f7fbfc] px-4 py-4 transition hover:border-[#0ea5b7] hover:bg-[#eef6f8]">
                {coverPreview ? (
                  <img src={coverPreview} alt="Kapak" className="h-16 w-24 rounded-xl object-cover" />
                ) : (
                  <div className="flex h-16 w-24 items-center justify-center rounded-xl bg-[#eef6f8] text-[#0ea5b7]">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                  </div>
                )}
                <div className="min-w-0">
                  <p className="text-sm font-bold text-[#174e67]">{coverPreview ? 'Değiştir' : 'Görsel ekle'}</p>
                  <p className="mt-0.5 text-xs text-[#67808c]">JPG, PNG, WebP — maks. 5 MB</p>
                </div>
                <input type="file" accept="image/*" onChange={handleCoverChange} className="hidden" />
              </label>
            </div>
          </div>

          {error ? (
            <div className="mt-5 rounded-2xl bg-[#fef4f4] px-4 py-3 text-sm font-semibold text-[#a04c4c]">{error}</div>
          ) : null}

          <div className="mt-6 flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-2xl border border-[#dbe7ec] bg-white px-4 py-3 text-sm font-bold text-[#174e67] transition hover:bg-[#eef6f8]"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={saving || !title.trim()}
              className="flex-1 rounded-2xl bg-gradient-to-br from-[#0ea5b7] to-[#174e67] px-4 py-3 text-sm font-black text-white shadow-[0_10px_28px_rgba(14,165,183,0.35)] transition active:scale-95 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving ? 'Kaydediliyor…' : isEdit ? 'Güncelle' : 'Oluştur'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CourseAdminDialog;
